import { toneNumbersPinyinText } from "@-/dev.improve.haniverse.database.schemas._lib.types.pinyin";
import { crudTable, index, text } from "@-/dev.improve.haniverse.lib.database.table";

/**
 * Which skill a word card drills. One card per `(word, pinyin, kind)`.
 *
 *   * `characters-in-context`     — given the word's pinyin plus a sentence for context, produce
 *                                   the word's characters. ("in context" = the sentence is shown to
 *                                   disambiguate a pinyin that several words could spell.)
 *   * `characters-in-sentence`    — given a sentence's English translation, produce the whole
 *                                   Chinese sentence by typing, one word at a time.
 *   * `pronunciation-in-sentence` — the same, produced by SPEAKING it.
 *
 * Declared as a `const` list rather than a bare union so the runtime values (drizzle's `enum`, zod
 * schemas, the export format) and the type stay in lockstep.
 */
export const WORD_CARD_KIND_LIST = [
  "characters-in-context",
  "characters-in-sentence",
  "pronunciation-in-sentence",
] as const;

export type WordCardKind = (typeof WORD_CARD_KIND_LIST)[number];

/**
 * A word card: the user recalls something about a word, shown in the context of a sentence that
 * uses it. Which *something* is the `kind` — see {@link WORD_CARD_KIND_LIST}.
 *
 * The SRS unit is `(word, kind)`, never the (word, sentence) pair — you are learning the word, and
 * each review picks a sentence containing it. That keeps card count bounded by vocabulary size and
 * matches FSRS semantics. Sentences deliberately have no card of their own (see `user_sentence`):
 * memorizing one specific sentence is not a goal.
 *
 * `kind` mirrors the meaning/pronunciation split grapheme cards already have across two tables:
 * producing a word's characters and producing its pronunciation are different skills that should
 * schedule independently, so they are separate cards with separate FSRS state. They share ONE
 * overlay table because they share their entire payload (`word`, `pinyin`, `description`) — the
 * grapheme side only uses two tables because a pronunciation card carries a `pinyin` a meaning
 * card does not.
 *
 * Word cards are structurally separate from grapheme cards — a different overlay table, its own
 * `user_card`, its own `fsrs_card` — so a word and a character are never the same card even when
 * they are the same text.
 *
 * A word card is identified by `(word, pinyin, kind)`: `(word, pinyin)` is the same rule
 * `(grapheme, pinyin)` gives pronunciation cards, so a word with two readings gets two cards, and
 * `kind` splits each of those into the separate skills it drills. Its row ids are derived from
 * that triple (`deriveWordSentenceCardRowIds`), so two devices that create the card before they
 * sync create the same rows. That is why no unique index guards the triple or `user_card_id`: sync
 * cannot resolve a unique conflict, so the duplicate is made impossible instead.
 */
export const userWordSentenceCardTable = crudTable(
  "user_word_sentence_card",
  {
    // A parent id, not a FOREIGN KEY: see `ACTOR_TABLE_PARENTS` (the cloud copy would enforce one).
    userCardId: text("user_card_id").notNull(),
    /** User-authored mnemonic for this word; null means use the catalog default. */
    description: text("description"),
    /** Which skill this card drills — see {@link WORD_CARD_KIND_LIST}. */
    // Spread into a mutable tuple: drizzle's `enum` option is typed as a mutable array, so the
    // `as const` list cannot be passed directly.
    kind: text("kind", { enum: [...WORD_CARD_KIND_LIST] }).notNull(),
    /**
     * Tone-number pinyin for the whole word.
     *
     * NOTE: unlike `user_grapheme_pronunciation_card.pinyin`, this is MULTI-SYLLABLE for a
     * multi-character word (e.g. `shui4 jiao4`). The single-syllable invariant that holds for
     * grapheme pronunciation cards does not apply here.
     */
    pinyin: toneNumbersPinyinText("pinyin").notNull(),
    word: text("word").notNull(),
  },
  // `userCardId` backs the `userCard.userWordSentenceCard` relation join; `word` backs the
  // per-word lookups.
  (table) => [
    index("user_word_sentence_card_user_card_id_idx").on(table.userCardId),
    index("user_word_sentence_card_word_idx").on(table.word),
  ],
);

export type UserWordSentenceCard = typeof userWordSentenceCardTable.$inferSelect;
